import {
  add,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  sub,
  type Duration,
} from 'date-fns';

import { DATE_TIME_REGEX } from '../shared/constants';
import type { DateTimePrecision } from '../shared/common';
import { createError } from '../shared/common';
import {
  formatDateTimeWithOffset,
  parseDateTime,
  parseDateTimeZoned,
  toOriginalTimeZoneNativeDate,
} from './lib';
import type { DateTimeZonedFormatTimeZone, DateTimeZonedType } from './types';

export const DateTimeZoned = {
  parse(value: string): DateTimeZonedType {
    if (typeof value !== 'string' || !DATE_TIME_REGEX.test(value)) {
      throw createError(
        `Ожидалась дата и время с таймзоной, получено: ${String(value)}`,
      );
    }
    parseDateTime(value);
    return value as DateTimeZonedType;
  },

  toNativeDate(value: DateTimeZonedType): Date {
    return parseDateTime(value);
  },

  fromNativeDate(
    value: Date,
    precision: DateTimePrecision = 'milliseconds',
    offsetSuffix = 'Z',
  ): DateTimeZonedType {
    return formatDateTimeWithOffset(
      value,
      precision,
      offsetSuffix,
    ) as DateTimeZonedType;
  },

  add(value: DateTimeZonedType, duration: Duration): DateTimeZonedType {
    const parsed = parseDateTimeZoned(this.parse(value));
    return this.fromNativeDate(
      add(parsed.date, duration),
      parsed.precision,
      parsed.offsetSuffix,
    );
  },

  sub(value: DateTimeZonedType, duration: Duration): DateTimeZonedType {
    const parsed = parseDateTimeZoned(this.parse(value));
    return this.fromNativeDate(
      sub(parsed.date, duration),
      parsed.precision,
      parsed.offsetSuffix,
    );
  },

  differenceInMilliseconds(
    left: DateTimeZonedType,
    right: DateTimeZonedType,
  ): number {
    return differenceInMilliseconds(
      this.toNativeDate(left),
      this.toNativeDate(right),
    );
  },

  differenceInMinutes(
    left: DateTimeZonedType,
    right: DateTimeZonedType,
  ): number {
    return differenceInMinutes(
      this.toNativeDate(left),
      this.toNativeDate(right),
    );
  },

  differenceInHours(left: DateTimeZonedType, right: DateTimeZonedType): number {
    return differenceInHours(this.toNativeDate(left), this.toNativeDate(right));
  },

  compare(left: DateTimeZonedType, right: DateTimeZonedType): number {
    const diff =
      this.toNativeDate(left).getTime() - this.toNativeDate(right).getTime();
    if (diff === 0) return 0;
    return diff > 0 ? 1 : -1;
  },

  format(
    value: DateTimeZonedType,
    locale: string,
    timeZone: DateTimeZonedFormatTimeZone,
    options: Intl.DateTimeFormatOptions,
  ): string {
    if (timeZone === 'original') {
      return new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: 'UTC',
      }).format(toOriginalTimeZoneNativeDate(value));
    }
    if (timeZone === 'local') {
      return new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(this.toNativeDate(value));
    }
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(
      this.toNativeDate(value),
    );
  },
};
